import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import PersonIcon from '@mui/icons-material/Person';
import React, { useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { GridColDef, getGridDateOperators } from '@mui/x-data-grid';
import dayjs from 'dayjs';

import { ROUTE } from 'route/RouteEnums';
import CustomDataGrid from 'utils/customDataGrid';

import DocumentActionIcons from './DocumentActionIcons';
import TruncatedTooltipCell from './TruncatedTooltipCell';
import type { DocumentReview } from '../types/documentReview.types';

interface DocumentTableProps {
  documents: DocumentReview[];
  loading: boolean;
  userId?: number;
  downloadingImagesDocumentId?: number | null;
  onViewPdf: (document: DocumentReview) => void;
  onViewImages: (document: DocumentReview) => void;
  onDownloadImages: (document: DocumentReview) => void;
  onApprove: (document: DocumentReview) => void;
  onReject: (document: DocumentReview) => void;
  onViewDetails: (document: DocumentReview) => void;
  onViewReason: (document: DocumentReview) => void;
}

const statusChipColor = (status: DocumentReview['reviewStatus']) => {
  switch (status) {
    case 'APPROVED':
      return 'success' as const;
    case 'REJECTED':
      return 'error' as const;
    default:
      return 'warning' as const;
  }
};

const formatStatusLabel = (status: DocumentReview['reviewStatus']) =>
  status.charAt(0) + status.slice(1).toLowerCase();

const DocumentTable: React.FC<DocumentTableProps> = ({
  documents,
  loading,
  userId,
  downloadingImagesDocumentId = null,
  onViewPdf,
  onViewImages,
  onDownloadImages,
  onApprove,
  onReject,
  onViewDetails,
  onViewReason,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const openPatientChart = (patientId: string | number) => {
    window.open(`${ROUTE.ADMIN_PATIENT_INFO}/${patientId}`, '_blank');
  };

  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'patientName',
        headerName: 'Patient Name',
        flex: 1.2,
        hideable: false,
        renderCell: (params) => {
          const row = params.row as DocumentReview;
          return (
            <span style={{ display: 'inline-flex', alignItems: 'center' }}>
              <Tooltip title='Patient Chart'>
                <IconButton
                  size='small'
                  aria-label='Patient Chart'
                  onClick={(event) => {
                    event.stopPropagation();
                    openPatientChart(row.patientId);
                  }}
                >
                  <PersonIcon fontSize='small' sx={{ color: '#3366FF' }} />
                </IconButton>
              </Tooltip>
              <span>{row.patientName}</span>
            </span>
          );
        },
      },
      {
        field: 'uploadedDate',
        headerName: 'Upload Date',
        flex: 0.8,
        type: 'date',
        filterOperators: getGridDateOperators().filter((operator) =>
          ['isNotEmpty', 'isEmpty', 'onOrBefore', 'onOrAfter', 'after', 'not', 'is'].includes(
            operator.value
          )
        ),
        valueGetter: (params) => params.row.uploadedDate,
        renderCell: (params) =>
          params.row.uploadedDate
            ? dayjs(params.row.uploadedDate).format('MM/DD/YYYY')
            : '-',
      },
      {
        field: 'documentName',
        headerName: 'Document Name',
        flex: 1.2,
        hideable: false,
        renderCell: (params) => (
          <TruncatedTooltipCell text={params.row.documentName} />
        ),
      },
      {
        field: 'documentDescription',
        headerName: 'Description',
        flex: 1.6,
        renderCell: (params) => (
          <TruncatedTooltipCell text={params.row.documentDescription} />
        ),
      },
      {
        field: 'documents',
        headerName: 'Documents',
        flex: 0.8,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const row = params.row as DocumentReview;
          const hasPdf = row.pdfAvailable ?? Boolean(row.generatedPdfUrl);
          const hasImages = row.rawImagesAvailable ?? row.rawImages.length > 0;

          return (
            <DocumentActionIcons
              hasPdf={hasPdf}
              hasImages={hasImages}
              documentId={row.documentId}
              downloadingImagesDocumentId={downloadingImagesDocumentId}
              onViewPdf={() => onViewPdf(row)}
              onViewImages={() => onViewImages(row)}
              onDownloadImages={() => onDownloadImages(row)}
            />
          );
        },
      },
      {
        field: 'actions',
        headerName: 'Actions',
        flex: 1.2,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const row = params.row as DocumentReview;
          if (row.reviewStatus === 'PENDING') {
            return (
              <Stack direction='row' spacing={1}>
                <Button variant='text' size='small' color='success' onClick={() => onApprove(row)}>
                  Approve
                </Button>
                <Button variant='text' size='small' color='error' onClick={() => onReject(row)}>
                  Reject
                </Button>
              </Stack>
            );
          }
          if (row.reviewStatus === 'APPROVED') {
            return (
              <Button variant='text' size='small' onClick={() => onViewDetails(row)}>
                View Details
              </Button>
            );
          }
          return (
            <Button variant='text' size='small' color='error' onClick={() => onViewReason(row)}>
              View Reason
            </Button>
          );
        },
      },
    ],
    [
      downloadingImagesDocumentId,
      onApprove,
      onDownloadImages,
      onReject,
      onViewDetails,
      onViewImages,
      onViewPdf,
      onViewReason,
    ]
  );

  const rows = useMemo(
    () => documents.map((doc) => ({ ...doc, id: doc.documentId })),
    [documents]
  );

  const getRowId = useMemo(
    () => (row: DocumentReview & { id: number }) => row.documentId,
    []
  );

  if (isMobile) {
    return (
      <Stack spacing={2}>
        {documents.length === 0 && !loading && (
          <Typography variant='body2' sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
            No documents found.
          </Typography>
        )}
        {documents.map((doc) => (
          <Card key={doc.documentId} variant='outlined'>
            <CardContent>
              <Stack spacing={1.5}>
                <Stack direction='row' justifyContent='space-between' alignItems='flex-start'>
                  <Box>
                    <Typography fontWeight={600}>{doc.patientName}</Typography>
                    <Typography variant='caption' color='text.secondary'>
                      {dayjs(doc.uploadedDate).format('MM/DD/YYYY')}
                    </Typography>
                  </Box>
                  <Chip
                    label={formatStatusLabel(doc.reviewStatus)}
                    color={statusChipColor(doc.reviewStatus)}
                    size='small'
                  />
                </Stack>
                <Typography variant='body2' fontWeight={500}>
                  {doc.documentName}
                </Typography>
                {doc.documentDescription && (
                  <Typography variant='body2' color='text.secondary'>
                    {doc.documentDescription}
                  </Typography>
                )}
                <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap>
                  {(doc.pdfAvailable ?? doc.generatedPdfUrl) && (
                    <Button variant='outlined' size='small' onClick={() => onViewPdf(doc)}>
                      View PDF
                    </Button>
                  )}
                  {(doc.rawImagesAvailable ?? doc.rawImages.length > 0) && (
                    <>
                      <Button variant='outlined' size='small' onClick={() => onViewImages(doc)}>
                        View Images ({doc.rawImageCount ?? doc.rawImages.length})
                      </Button>
                      <Button
                        variant='outlined'
                        size='small'
                        onClick={() => onDownloadImages(doc)}
                        disabled={downloadingImagesDocumentId !== null}
                        startIcon={
                          downloadingImagesDocumentId === doc.documentId ? (
                            <CircularProgress size={14} color='inherit' />
                          ) : (
                            <FileDownloadOutlinedIcon fontSize='small' />
                          )
                        }
                      >
                        Download Images
                      </Button>
                    </>
                  )}
                </Stack>
                <Stack direction='row' spacing={1}>
                  {doc.reviewStatus === 'PENDING' && (
                    <>
                      <Button variant='contained' size='small' color='success' onClick={() => onApprove(doc)}>
                        Approve
                      </Button>
                      <Button variant='outlined' size='small' color='error' onClick={() => onReject(doc)}>
                        Reject
                      </Button>
                    </>
                  )}
                  {doc.reviewStatus === 'APPROVED' && (
                    <Button variant='text' size='small' onClick={() => onViewDetails(doc)}>
                      View Details
                    </Button>
                  )}
                  {doc.reviewStatus === 'REJECTED' && (
                    <Button variant='text' size='small' color='error' onClick={() => onViewReason(doc)}>
                      View Reason
                    </Button>
                  )}
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Stack>
    );
  }

  return (
    <CustomDataGrid
      rows={rows}
      columns={columns}
      dashBordKey='DocumentReview'
      userId={userId}
      loading={loading}
      getRowId={getRowId}
      setting={{ disableExport: true }}
    />
  );
};

export default DocumentTable;
